import { REVENUE_INTELLIGENCE_COPY } from "../lib/revenueIntelligenceCopy";

export type ForecastBaselineSource = "crm_stage_weighted" | "hubspot_submission";

export type ForecastDiagnosisDeal = {
  adjustedCloseDate: string | null;
  baselineContribution: number;
  driverSignals?: Record<string, string>;
  evidenceCloseInPeriod: boolean;
  evidenceContribution: number;
  forecastCategory: string | null;
  fullValue: number;
  id: string;
  name: string;
  nextStep: string | null;
  nextStepUpdatedAt: string | null;
  signal: string;
  stage: string;
  upsideDelta: number;
};

export type ForecastDiagnosisMetric = { label: string; value: string };

export type ForecastDiagnosisDriver = {
  allDealIds?: string[];
  dealIds: string[];
  explanation: string;
  flowProminence: "primary" | "major" | "material" | "supporting";
  flowSummary: string;
  headline: string;
  id: string;
  label: string;
  metrics: ForecastDiagnosisMetric[];
  status: "weak" | "watch" | "supportive" | "unavailable";
};

export type ForecastDiagnosisScenarioCondition = {
  condition: string;
  impact?: number;
  impactCondition?: string;
  dealCount?: number;
  dealId: string | null;
  dealIds?: string[];
  dealName: string | null;
  eventType?: "lost" | "moved-out" | "moved-in" | "won";
  stage: string | null;
  valueExplanation: string | null;
};

export type ForecastDiagnosisScenario = {
  conditions: ForecastDiagnosisScenarioCondition[];
  dealIds: string[];
  delta: number;
  kind: "downside" | "expected" | "upside";
  label: string;
  summary: string;
  value: number;
};

export type ForecastGapDeal = {
  baselineContribution: number;
  crmCloseDate: string | null;
  evidenceContribution: number;
  fullValue: number;
  gapContribution: number;
  id: string;
  name: string;
  revelateClose: string | null;
  stage: string;
};

export type ForecastGapGroup = {
  dealIds: string[];
  deals: ForecastGapDeal[];
  direction: "below" | "above";
  fullDealValue: number;
  gapContribution: number;
  id:
    | "moved-out-of-period"
    | "crm-overstates-value"
    | "revelate-above-crm";
  label: string;
  summary: string;
};

export type ForecastDiagnosisStory = {
  baseline: {
    asOf: string | null;
    description: string;
    label: string;
    source: ForecastBaselineSource;
  };
  baselineForecast: number;
  confidence: {
    change: number | null;
    changePeriod: string | null;
    label: "Strong" | "Caution" | "Weak" | "Unavailable";
    score: number | null;
  };
  currencyCode: string;
  dataMode: "demo" | "live";
  deals: ForecastDiagnosisDeal[];
  drivers: ForecastDiagnosisDriver[];
  expectedOutcome: number;
  gapBreakdown: {
    groups: ForecastGapGroup[];
    netGap: number;
  } | null;
  forecastContext: {
    evidenceWeightedCoverage: {
      coverageMultiple: number | null;
      denominatorRemainingTarget: number;
      eligibleDealCount: number;
      evidenceWeightedPipelineValue: number;
      status: "covered" | "gap" | "target_achieved";
    } | null;
    remainingTarget: number | null;
    wonToDate: number;
  };
  headline: string;
  likelyRange: { high: number; low: number };
  period: string;
  periodClosed: boolean;
  scenarios: ForecastDiagnosisScenario[];
  summary: string;
  target: number | null;
};

const deals: ForecastDiagnosisDeal[] = [
  { adjustedCloseDate: "2026-09-22T00:00:00.000Z", baselineContribution: 1_550_000, evidenceCloseInPeriod: true, evidenceContribution: 850_000, forecastCategory: "Commit", fullValue: 2_000_000, id: "liberty", name: "Liberty", nextStep: null, nextStepUpdatedAt: null, signal: "Decision activity stalled and the close date moved by 14 days.", stage: "Commit", upsideDelta: 1_150_000 },
  { adjustedCloseDate: "2026-09-18T00:00:00.000Z", baselineContribution: 800_000, evidenceCloseInPeriod: true, evidenceContribution: 300_000, forecastCategory: "Commit", fullValue: 1_000_000, id: "nordic-grid", name: "NordicGrid", nextStep: null, nextStepUpdatedAt: null, signal: "Buyer engagement fell while the opportunity remained in Commit.", stage: "Commit", upsideDelta: 700_000 },
  { adjustedCloseDate: "2026-09-27T00:00:00.000Z", baselineContribution: 650_000, evidenceCloseInPeriod: true, evidenceContribution: 300_000, forecastCategory: null, fullValue: 900_000, id: "horizon", name: "Horizon Health", nextStep: null, nextStepUpdatedAt: null, signal: "Late-stage progression is 11 days behind the historical pattern.", stage: "Proposal", upsideDelta: 600_000 },
  { adjustedCloseDate: "2026-09-29T00:00:00.000Z", baselineContribution: 700_000, evidenceCloseInPeriod: true, evidenceContribution: 450_000, forecastCategory: null, fullValue: 900_000, id: "atlas", name: "Atlas Manufacturing", nextStep: null, nextStepUpdatedAt: null, signal: "Commercial activity is present, but progression remains slower than normal.", stage: "Proposal", upsideDelta: 450_000 },
  { adjustedCloseDate: "2026-09-24T00:00:00.000Z", baselineContribution: 650_000, evidenceCloseInPeriod: true, evidenceContribution: 450_000, forecastCategory: "Commit", fullValue: 800_000, id: "vertex", name: "Vertex Systems", nextStep: null, nextStepUpdatedAt: null, signal: "Evidence supports partial contribution, not the full CRM-weighted amount.", stage: "Negotiation", upsideDelta: 350_000 },
  { adjustedCloseDate: "2026-09-30T00:00:00.000Z", baselineContribution: 650_000, evidenceCloseInPeriod: true, evidenceContribution: 350_000, forecastCategory: null, fullValue: 800_000, id: "remaining", name: "Remaining forecast opportunities", nextStep: null, nextStepUpdatedAt: null, signal: "The remaining population converts below the CRM-weighted value.", stage: "Mixed", upsideDelta: 450_000 },
];

export const forecastDiagnosisDemoStory: ForecastDiagnosisStory = {
  baseline: {
    asOf: null,
    description: "Stage-weighted value from the CRM pipeline.",
    label: "CRM stage-weighted forecast",
    source: "crm_stage_weighted",
  },
  baselineForecast: 5_000_000,
  confidence: { change: null, changePeriod: null, label: "Weak", score: 54 },
  currencyCode: "EUR",
  dataMode: "demo",
  deals,
  drivers: [
    {
      dealIds: ["liberty"],
      explanation: "Shows how much of the forecast depends on the largest Deals.",
      flowProminence: "primary",
      flowSummary: "31% depends on Liberty",
      headline: "31% of the remaining forecast depends on Liberty.",
      id: "revenue-concentration",
      label: "Revenue concentration",
      metrics: [
        { label: "Share of remaining forecast", value: "31%" },
        { label: "Included in forecast", value: "€0.85M" },
        { label: "Full deal value", value: "€2.00M" },
      ],
      status: "weak",
    },
    {
      dealIds: ["liberty", "nordic-grid"],
      explanation: "These Deals lost forecast support during the last 30 days.",
      flowProminence: "major",
      flowSummary: "Forecast weakened",
      headline: "Two material opportunities weakened during the last 30 days.",
      id: "forecast-weakened",
      label: "Forecast weakened",
      metrics: [
        { label: "Affected Deals", value: "2" },
        { label: "Current evidence contribution", value: "€1.15M" },
        { label: "Assessment", value: "Material" },
      ],
      status: "weak",
    },
    {
      dealIds: ["horizon", "atlas"],
      explanation: "Deals are moving more slowly than the CRM forecast assumes.",
      flowProminence: "material",
      flowSummary: "Late stage is 13 days behind",
      headline: "Late-stage Deals are moving too slowly.",
      id: "deal-velocity",
      label: "Deal progress",
      metrics: [
        { label: "Current late-stage pace", value: "42 days" },
        { label: "Historical won pattern", value: "29 days" },
        { label: "Affected baseline", value: "€1.35M" },
      ],
      status: "watch",
    },
  ],
  expectedOutcome: 2_700_000,
  gapBreakdown: null,
  forecastContext: {
    evidenceWeightedCoverage: {
      coverageMultiple: 2_700_000 / 5_200_000,
      denominatorRemainingTarget: 5_200_000,
      eligibleDealCount: 6,
      evidenceWeightedPipelineValue: 2_700_000,
      status: "gap",
    },
    remainingTarget: 5_200_000,
    wonToDate: 0,
  },
  headline: "The CRM baseline is €5.0M. The evidence currently supports €2.7M.",
  likelyRange: { high: 3_200_000, low: 2_400_000 },
  period: "Q3 2026 · POC scenario",
  periodClosed: false,
  scenarios: [
    {
      conditions: [{ condition: "Current Deal signals and expected close dates hold.", dealId: null, dealName: null, stage: null, valueExplanation: null }],
      dealIds: [], delta: 0, kind: "expected", label: REVENUE_INTELLIGENCE_COPY.forecast.revelateForecast, summary: "Current reportable forecast", value: 2_700_000,
    },
    {
      conditions: [{ condition: "Won this quarter.", dealId: "liberty", dealName: "Liberty", stage: "Commit", valueExplanation: "Decision activity stalled. The Revelate forecast includes €0.85M from this Deal. Winning it this quarter would add €0.50M." }],
      dealIds: ["liberty"], delta: 500_000, kind: "upside", label: "Upside case", summary: "If the strongest opportunities recover", value: 3_200_000,
    },
    {
      conditions: [{ condition: "Lost.", dealId: "liberty", dealName: "Liberty", stage: "Commit", valueExplanation: "Decision activity stalled. The Revelate forecast includes €0.30M from this Deal. This case removes it." }],
      dealIds: ["liberty"], delta: -300_000, kind: "downside", label: "Downside case", summary: "If the current risks materialize", value: 2_400_000,
    },
  ],
  summary: "The evidence-based forecast is below the CRM baseline because the quarter depends heavily on a small number of unstable late-stage opportunities.",
  target: 5_200_000,
};

export function getForecastAgreementScore(baseline: number, evidenceForecast: number) {
  if (baseline === 0 && evidenceForecast === 0) return null;
  const largest = Math.max(Math.abs(baseline), Math.abs(evidenceForecast));
  if (largest === 0) return null;
  return Math.round(100 * Math.min(Math.abs(baseline), Math.abs(evidenceForecast)) / largest);
}

export function getForecastAgreementLabel(score: number | null): ForecastDiagnosisStory["confidence"]["label"] {
  if (score === null) return "Unavailable";
  if (score >= 85) return "Strong";
  if (score >= 65) return "Caution";
  return "Weak";
}

export function getForecastDiagnosisReconciliationErrors(story: ForecastDiagnosisStory) {
  const errors: string[] = [];
  const scenarios = new Map(story.scenarios.map(scenario => [scenario.kind, scenario]));
  const expectedScenario = scenarios.get("expected");
  const downsideScenario = scenarios.get("downside");
  const upsideScenario = scenarios.get("upside");
  const agreementScore = getForecastAgreementScore(story.baselineForecast, story.expectedOutcome);

  if (story.confidence.score !== agreementScore) errors.push("Agreement index does not reconcile to the two forecast endpoints.");
  if (expectedScenario?.value !== story.expectedOutcome || expectedScenario?.delta !== 0) errors.push("Expected scenario does not match the Revelate forecast.");
  if (downsideScenario?.value !== story.likelyRange.low) errors.push("Downside scenario does not match the range low.");
  if (upsideScenario?.value !== story.likelyRange.high) errors.push("Upside scenario does not match the range high.");
  if (downsideScenario && downsideScenario.value !== story.expectedOutcome + downsideScenario.delta) errors.push("Downside scenario does not reconcile to its incremental delta.");
  if (upsideScenario && upsideScenario.value !== story.expectedOutcome + upsideScenario.delta) errors.push("Upside scenario does not reconcile to its incremental delta.");

  return errors;
}
